const DiscountPromotionInfluencer = (
	json: IDiscountPromotionInfluencerDetails,
): IDiscountPromotionInfluencerDetailsModel => {
	const { first_name, last_name } = json

	return {
		firstName: first_name,
		lastName: last_name,
	}
}

export const DiscountPromotion = (json: IDiscountPromotion): IDiscountPromotionModel => {
	const {
		is_exclusive,
		usage_count,
		usage_limit,
		is_coupon_based,
		starts_at,
		ends_at,
		applies_to_discounted,
		created_at,
		updated_at,
		actions,
		coupons,
		rules,
		is_active,
		influencer_name,
		influencer_client_id,
		influencer_client_profile,
		is_valid,
		is_limited_to_users,
		is_purchasable_with_points,
		points_price,
		points_promotion_type,
		...rest
	} = json

	return {
		usageCount: usage_count,
		usageLimit: usage_limit,
		startsAt: starts_at ? new Date(starts_at) : null,
		endsAt: ends_at ? new Date(ends_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
		isValid: is_valid,
		isActive: is_active,
		isExclusive: is_exclusive,
		isCouponBased: is_coupon_based,
		appliesToDiscounted: applies_to_discounted,
		actions: actions ? actions.map(PromotionAction) : [],
		coupons: coupons ? coupons.map(Coupon) : [],
		rules: rules ? rules.map(DiscountPromotionRule) : [],
		influencerName: influencer_name ?? null,
		influencerClientID: influencer_client_id ?? null,
		influencerClientProfile: influencer_client_profile
			? DiscountPromotionInfluencer(influencer_client_profile)
			: null,
		isLimitedToUsers: is_limited_to_users,
		isPurchasableWithPoints: is_purchasable_with_points,
		pointsPrice: points_price ?? null,
		pointsPromotionType: points_promotion_type ?? null,
		...rest,
	}
}

export const DiscountPromotionShared = (json: IDiscountPromotionShared): IDiscountPromotionSharedModel => {
	const {
		id,
		name,
		description,
		influencer_name,
		influencer_client_id,
		influencer_client_profile,
		commission,
		is_active,
		actions,
		rules,
		starts_at,
		ends_at,
		updated_at,
		created_at,
	} = json

	return {
		id,
		name,
		description,
		influencerName: influencer_name ?? null,
		influencerClientID: influencer_client_id ?? null,
		influencerClientProfile: influencer_client_profile
			? DiscountPromotionInfluencer(influencer_client_profile)
			: null,
		commission: commission ?? null,
		isActive: is_active,
		actions: actions ? actions.map(PromotionAction) : [],
		rules: rules ? rules.map(DiscountPromotionRule) : [],
		startsAt: starts_at ? new Date(starts_at) : null,
		endsAt: ends_at ? new Date(ends_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const promotionToJSON = (obj: Partial<IDiscountPromotionModel>): Partial<IDiscountPromotion> => {
	const { isCouponBased, isExclusive, appliesToDiscounted, usageLimit, startsAt, endsAt, isActive, ...rest } = obj
	return {
		...rest,
		is_coupon_based: isCouponBased ?? true,
		is_exclusive: isExclusive ?? false,
		applies_to_discounted: appliesToDiscounted ?? false,
		usage_limit: usageLimit ?? null,
		is_active: isActive ?? false,
		starts_at: startsAt ? new Date(startsAt).toISOString() : null,
		ends_at: endsAt ? new Date(endsAt).toISOString() : null,
	}
}

/**
 * History
 */
export const PromotionHistory = (json: IDiscountPromotionHistory): IDiscountPromotionHistoryModel => {
	const { id, entity_id, action, date, description, editor } = json

	return {
		id,
		action,
		description,
		editor,
		entityID: entity_id,
		date: date ? new Date(date) : null,
	}
}

/**
 * List models
 */
export const CouponListItemAction = (
	json: IDiscountCouponListItemPromotionAction,
): IDiscountCouponListItemPromotionActionModel => ({
	id: json.id,
	title: json.title,
	type: json.type,
	configuration: json.configuration,
})

const CouponListItemPromotionInfluencer = (
	json: IDiscountCouponListItemPromotionInfluencer,
): IDiscountCouponListItemPromotionInfluencerModel => {
	const { first_name, last_name } = json

	return {
		firstName: first_name,
		lastName: last_name,
	}
}

const CouponListItemPromotion = (json: IDiscountCouponListItemPromotion): IDiscountCouponListItemPromotionModel => {
	const {
		is_active,
		actions,
		commission,
		is_influencer,
		influencer_name,
		influencer_client_id,
		influencer_client_profile,
		ends_at,
		created_at,
		...rest
	} = json

	return {
		...rest,
		isActive: is_active,
		actions: actions ? actions.map(CouponListItemAction) : [],
		isInfluencer: is_influencer ?? false,
		influencerName: influencer_name ?? null,
		influencerClientID: influencer_client_id ?? null,
		commission: commission ?? null,
		influencerClientProfile: influencer_client_profile
			? CouponListItemPromotionInfluencer(influencer_client_profile)
			: null,
		endsAt: ends_at ? new Date(ends_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const CouponListItem = (json: IDiscountCouponListItem): IDiscountCouponListItemModel => {
	const { promotion_id, promotion, usage_count, usage_limit, created_at, ...rest } = json

	return {
		...rest,
		usageCount: usage_count,
		usageLimit: usage_limit,
		promotionId: promotion_id,
		promotion: CouponListItemPromotion(promotion),
		createdAt: created_at,
	}
}

/**
 * Payloads
 */
const DiscountPromotionPayloadShared = (
	event: IFormSubmitEvent<IDiscountPromotionFormData>,
	options?: IPromotionPayloadOptions,
): IPromotionPayloadShared => {
	const { name, description, isActive, isExclusive, isCouponBased, usageLimit, startsAt, endsAt } = event.values

	const starts_at = startsAt ? formatDateForPayload(startsAt) : null
	const ends_at = endsAt ? formatDateForPayload(endsAt) : null

	const obj = {
		name,
		description,
		priority: 0,
		is_active: isActive,
		is_exclusive: isExclusive ?? false,
		is_coupon_based: isCouponBased ?? true,
		usage_limit: usageLimit ?? null,
		starts_at: starts_at ? starts_at.toISOString() : null,
		ends_at: ends_at ? ends_at.toISOString() : null,
		applies_to_discounted: false,
		points_price: null,
		points_promotion_type: null,
		is_influencer: options?.isInfluencer ?? false,
		properties: null,
	}

	return obj as IPromotionPayloadShared
}

export const DiscountPromotionCreatePayload = (
	event: IFormSubmitEvent<IDiscountPromotionFormData>,
): IPromotionCreatePayload => {
	const shared = DiscountPromotionPayloadShared(event)

	return {
		...shared,
	}
}

export const DiscountPromotionUpdatePayload = (
	event: IFormSubmitEvent<IDiscountPromotionFormData>,
): IPromotionUpdatePayload => {
	const shared = DiscountPromotionPayloadShared(event)

	return {
		...shared,
	}
}

const DiscountPromotionInfluencerPayloadShared = (
	event: IFormSubmitEvent<IDiscountInfluencersFormData>,
	options: IPromotionPayloadOptions,
): IPromotionPayloadShared => {
	const { influencerName, influencerClientID, commission } = event.values

	const shared = DiscountPromotionPayloadShared(event, options)

	return {
		...shared,
		influencer_name: influencerName ?? null,
		influencer_client_id: influencerClientID ?? null,
		commission: commission ?? null,
	}
}

export const DiscountPromotionInfluencerCreatePayload = (
	event: IFormSubmitEvent<IDiscountInfluencersFormData>,
	options: IPromotionPayloadOptions,
): IPromotionCreatePayload => {
	const shared = DiscountPromotionInfluencerPayloadShared(event, options)

	return {
		...shared,
	}
}

export const DiscountPromotionInfluencerUpdatePayload = (
	event: IFormSubmitEvent<IDiscountInfluencersFormData>,
	options: IPromotionPayloadOptions,
): IPromotionUpdatePayload => {
	const shared = DiscountPromotionInfluencerPayloadShared(event, options)

	return {
		...shared,
	}
}
